"use client";

import { c as _c } from "react/compiler-runtime";
import MediaDisplay from "@/components/drops/view/item/content/media/MediaDisplay";
import CommonDropdownItemsMobileWrapper from "@/components/utils/select/dropdown/CommonDropdownItemsMobileWrapper";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import DropCurationButton from "@/components/waves/drops/DropCurationButton";
import WaveDropActionsOpen from "@/components/waves/drops/WaveDropActionsOpen";
import WaveDropMobileMenuOpen from "@/components/waves/drops/WaveDropMobileMenuOpen";
import WinnerDropBadge from "@/components/waves/drops/winner/WinnerDropBadge";
import WaveDropPartContentMarkdown from "@/components/waves/drops/WaveDropPartContentMarkdown";
import { LinkPreviewProvider } from "@/components/waves/LinkPreviewContext";
import { ApiDropType } from "@/generated/models/ApiDropType";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { getDropPreviewImageUrl } from "@/helpers/waves/drop.helpers";
import { ImageScale } from "@/helpers/image.helpers";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import { useDropCurationMutation } from "@/hooks/drops/useDropCurationMutation";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import useLongPressInteraction from "@/hooks/useLongPressInteraction";
import { startDropOpen } from "@/utils/monitoring/dropOpenTiming";
import Link from "next/link";
import React, { useCallback, useMemo, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import WaveLeaderboardGalleryItemVotes from "../gallery/WaveLeaderboardGalleryItemVotes";
import { WaveLeaderboardIdentity } from "../identity/WaveLeaderboardIdentity";
import type { WaveLeaderboardGridMode } from "./WaveLeaderboardGrid";
interface WaveLeaderboardGridItemProps {
  readonly drop: ExtendedDrop;
  readonly mode: WaveLeaderboardGridMode;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
const getVoteStyle = (userVote: number): string => userVote <= 0 ? "tw-text-iron-400" : "tw-text-iron-300";
const canOpenGridItemFromClick = ({
  isMenuOpen,
  target
}: {
  readonly isMenuOpen: boolean;
  readonly target: HTMLElement;
}): boolean => {
  if (isMenuOpen) {
    return false;
  }
  return !target.closest("a, button");
};
const isGridItemOpenKey = (key: string): boolean => key === "Enter" || key === " ";
const getGridViewportClassName = (isCompactMode: boolean): string => isCompactMode ? "tw-relative tw-overflow-hidden tw-bg-iron-950" : "tw-relative tw-max-h-[20rem] tw-overflow-hidden tw-bg-iron-950";
const getGridContentSpacingClassName = (isCompactMode: boolean): string => isCompactMode ? "tw-space-y-3" : "tw-space-y-1";
const getGridMediaWrapperClassName = (isCompactMode: boolean): string => isCompactMode ? "tw-overflow-hidden tw-rounded-lg tw-bg-iron-900" : "tw-overflow-hidden tw-bg-iron-950";
const getGridTextWrapperClassName = ({
  hasMedia,
  isCompactMode
}: {
  readonly hasMedia: boolean;
  readonly isCompactMode: boolean;
}): string => `tw-px-3 ${hasMedia ? "tw-pt-2" : "tw-pt-3"} ${isCompactMode ? "tw-pb-4" : "tw-pb-3"}`;
const getCompactTextViewportClassName = (isCompactMode: boolean): string | undefined => isCompactMode ? "tw-relative tw-max-h-56 tw-overflow-hidden [&_p]:tw-whitespace-normal" : undefined;
function GridItemRankBadge(t0) {
  const $ = _c(4);
  const {
    drop
  } = t0;
  if (drop.rank === null) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="tw-flex tw-h-6 tw-min-w-6 tw-items-center tw-justify-center tw-rounded-xl tw-bg-iron-800 tw-px-2 tw-text-xs tw-font-semibold tw-text-iron-400">-</div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const t1 = drop.winning_context?.decision_time ?? null;
  let t2;
  if ($[1] !== drop.rank || $[2] !== t1) {
    t2 = <WinnerDropBadge rank={drop.rank} decisionTime={t1} />;
    $[1] = drop.rank;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function useOverflowGradient(t0) {
  const $ = _c(6);
  const {
    viewportEl,
    innerEl
  } = t0;
  let t1;
  if ($[0] !== innerEl || $[1] !== viewportEl) {
    t1 = () => {
      if (!viewportEl || !innerEl) {
        return false;
      }
      return innerEl.scrollHeight > viewportEl.clientHeight + 1;
    };
    $[0] = innerEl;
    $[1] = viewportEl;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const getOverflowSnapshot = t1;
  let t2;
  if ($[3] !== innerEl || $[4] !== viewportEl) {
    t2 = onStoreChange => {
      if (!viewportEl || !innerEl) {
        return _temp;
      }
      if (typeof ResizeObserver === "undefined") {
        window.addEventListener("resize", onStoreChange);
        return () => {
          window.removeEventListener("resize", onStoreChange);
        };
      }
      const observer = new ResizeObserver(() => {
        onStoreChange();
      });
      observer.observe(viewportEl);
      observer.observe(innerEl);
      return () => {
        observer.disconnect();
      };
    };
    $[3] = innerEl;
    $[4] = viewportEl;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const subscribeToOverflow = t2;
  return useSyncExternalStore(subscribeToOverflow, getOverflowSnapshot, _temp2);
}
function _temp2() {
  return false;
}
function _temp() {}
export const WaveLeaderboardGridItem: React.FC<WaveLeaderboardGridItemProps> = t0 => {
  const $ = _c(111);
  const {
    drop,
    mode,
    onDropClick
  } = t0;
  const isCompactMode = mode === "compact";
  const isContentOnlyMode = mode === "content_only";
  const activePart = drop.parts[0];
  const author = drop.author;
  const authorHandle = author.handle ?? null;
  const primaryMedia = activePart?.media[0];
  const isCuratable = drop.context_profile_context?.curatable ?? false;
  const isCurated = drop.context_profile_context?.curated ?? false;
  const canOpenDrop = drop.drop_type !== ApiDropType.Chat;
  const isMobileScreen = useIsMobileScreen();
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const {
    toggleCuration,
    isPending: isCurating
  } = useDropCurationMutation();
  let t1;
  if ($[0] !== hasTouchScreen) {
    t1 = {
      hasTouchScreen,
      preventDefault: false
    };
    $[0] = hasTouchScreen;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isActive,
    setIsActive,
    touchHandlers
  } = useLongPressInteraction(t1);
  const [isVotingModalOpen, setIsVotingModalOpen] = useState(false);
  const {
    canShowVote
  } = useDropInteractionRules(drop);
  const [viewportEl, setViewportEl] = useState(null);
  const [innerEl, setInnerEl] = useState(null);
  const [compactTextViewportEl, setCompactTextViewportEl] = useState(null);
  const [compactTextInnerEl, setCompactTextInnerEl] = useState(null);
  const hasContentOnlyActions = canOpenDrop || isCuratable || canShowVote;
  const showDesktopContentOnlyActions = isContentOnlyMode && !hasTouchScreen && hasContentOnlyActions;
  const showMobileContentOnlyActions = isContentOnlyMode && hasTouchScreen && hasContentOnlyActions;
  let t2;
  if ($[2] !== drop.metadata) {
    t2 = getDropPreviewImageUrl(drop.metadata);
    $[2] = drop.metadata;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const previewImageUrl = t2;
  const mediaUrl = primaryMedia?.url ?? previewImageUrl ?? null;
  const mediaMimeType = primaryMedia?.mime_type ?? "image/jpeg";
  let t3;
  if ($[4] !== innerEl || $[5] !== viewportEl) {
    t3 = {
      viewportEl,
      innerEl
    };
    $[4] = innerEl;
    $[5] = viewportEl;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const showGradient = useOverflowGradient(t3);
  let t4;
  if ($[7] !== compactTextInnerEl || $[8] !== compactTextViewportEl) {
    t4 = {
      viewportEl: compactTextViewportEl,
      innerEl: compactTextInnerEl
    };
    $[7] = compactTextInnerEl;
    $[8] = compactTextViewportEl;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const showCompactTextGradient = useOverflowGradient(t4);
  const hasUserVoted = drop.context_profile_context?.rating !== undefined;
  const userVote = drop.context_profile_context?.rating ?? 0;
  const isNegativeVote = userVote < 0;
  let t5;
  if ($[10] !== userVote) {
    t5 = getVoteStyle(userVote);
    $[10] = userVote;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const voteStyle = t5;
  const votingCreditType = drop.wave.voting_credit_type;
  const votingCreditLabels = WAVE_VOTING_LABELS as Partial<Record<typeof votingCreditType, string>>;
  const votingCreditLabel = votingCreditLabels[votingCreditType] ?? votingCreditType;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsVotingModalOpen(true);
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleVoteButtonClick = t6;
  let t7;
  if ($[13] !== drop.id || $[14] !== drop.wave.id || $[15] !== isCuratable || $[16] !== isCurated || $[17] !== setIsActive || $[18] !== toggleCuration) {
    t7 = () => {
      if (!isCuratable || drop.id.startsWith("temp-")) {
        return;
      }
      toggleCuration({
        dropId: drop.id,
        waveId: drop.wave.id,
        isCuratable,
        isCurated
      });
      setIsActive(false);
    };
    $[13] = drop.id;
    $[14] = drop.wave.id;
    $[15] = isCuratable;
    $[16] = isCurated;
    $[17] = setIsActive;
    $[18] = toggleCuration;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const handleMobileCurateClick = t7;
  let t8;
  if ($[20] !== setIsActive) {
    t8 = () => {
      setIsActive(false);
      setIsVotingModalOpen(true);
    };
    $[20] = setIsActive;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const handleMobileVoteClick = t8;
  let t9;
  if ($[22] !== drop || $[23] !== isMobileScreen || $[24] !== onDropClick) {
    t9 = () => {
      startDropOpen({
        dropId: drop.id,
        waveId: drop.wave.id,
        source: "leaderboard_grid",
        isMobile: isMobileScreen
      });
      onDropClick(drop);
    };
    $[22] = drop;
    $[23] = isMobileScreen;
    $[24] = onDropClick;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const openDrop = t9;
  let t10;
  if ($[26] !== isActive || $[27] !== openDrop || $[28] !== showMobileContentOnlyActions) {
    t10 = event => {
      const target = event.target as HTMLElement;
      if (!canOpenGridItemFromClick({
        isMenuOpen: showMobileContentOnlyActions && isActive,
        target
      })) {
        return;
      }
      openDrop();
    };
    $[26] = isActive;
    $[27] = openDrop;
    $[28] = showMobileContentOnlyActions;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const onCardClick = t10;
  let t11;
  if ($[30] !== isActive || $[31] !== openDrop || $[32] !== showMobileContentOnlyActions) {
    t11 = event_0 => {
      if (!isGridItemOpenKey(event_0.key)) {
        return;
      }
      const target_0 = event_0.target as HTMLElement;
      if (!canOpenGridItemFromClick({
        isMenuOpen: showMobileContentOnlyActions && isActive,
        target: target_0
      })) {
        return;
      }
      event_0.preventDefault();
      openDrop();
    };
    $[30] = isActive;
    $[31] = openDrop;
    $[32] = showMobileContentOnlyActions;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const onCardKeyDown = t11;
  const t12 = `wave-leaderboard-grid-item-${drop.id}`;
  let t13;
  if ($[34] !== showMobileContentOnlyActions || $[35] !== touchHandlers) {
    t13 = showMobileContentOnlyActions ? touchHandlers : {};
    $[34] = showMobileContentOnlyActions;
    $[35] = touchHandlers;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== isCompactMode) {
    t14 = getGridViewportClassName(isCompactMode);
    $[37] = isCompactMode;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== isCompactMode) {
    t15 = getGridContentSpacingClassName(isCompactMode);
    $[39] = isCompactMode;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== isCompactMode || $[42] !== mediaMimeType || $[43] !== mediaUrl || $[44] !== previewImageUrl) {
    t16 = mediaUrl && <div className={getGridMediaWrapperClassName(isCompactMode)}><MediaDisplay media_mime_type={mediaMimeType} media_url={mediaUrl} disableMediaInteraction={true} imageScale={ImageScale.AUTOx450} previewImageUrl={previewImageUrl} /></div>;
    $[41] = isCompactMode;
    $[42] = mediaMimeType;
    $[43] = mediaUrl;
    $[44] = previewImageUrl;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== activePart || $[47] !== drop || $[48] !== isCompactMode || $[49] !== mediaUrl || $[50] !== showCompactTextGradient) {
    t17 = activePart && <div className={getGridTextWrapperClassName({
      hasMedia: mediaUrl !== null,
      isCompactMode
    })}><div ref={isCompactMode ? setCompactTextViewportEl : undefined} className={getCompactTextViewportClassName(isCompactMode)}><div ref={isCompactMode ? setCompactTextInnerEl : undefined}><LinkPreviewProvider variant="home"><WaveDropPartContentMarkdown mentionedUsers={drop.mentioned_users} mentionedGroups={drop.mentioned_groups} mentionedWaves={drop.mentioned_waves} referencedNfts={drop.referenced_nfts} part={activePart} wave={drop.wave} drop={drop} onQuoteClick={_temp3} /></LinkPreviewProvider></div>{isCompactMode && showCompactTextGradient && <div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-14 tw-bg-gradient-to-t tw-from-iron-950 tw-via-iron-950/70 tw-to-transparent" />}</div></div>;
    $[46] = activePart;
    $[47] = drop;
    $[48] = isCompactMode;
    $[49] = mediaUrl;
    $[50] = showCompactTextGradient;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== t15 || $[53] !== t16 || $[54] !== t17) {
    t18 = <div ref={setInnerEl} className={t15}>{t16}{t17}</div>;
    $[52] = t15;
    $[53] = t16;
    $[54] = t17;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== showGradient) {
    t19 = showGradient && <div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-14 tw-bg-gradient-to-t tw-from-iron-900 tw-via-iron-900/90 tw-to-transparent" />;
    $[56] = showGradient;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== canOpenDrop || $[59] !== canShowVote || $[60] !== drop || $[61] !== isCuratable || $[62] !== isCurated || $[63] !== showDesktopContentOnlyActions) {
    t20 = showDesktopContentOnlyActions && <div data-testid={`wave-leaderboard-grid-item-content-only-actions-${drop.id}`} className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-z-0 tw-bg-gradient-to-t tw-from-black/90 tw-via-black/65 tw-to-transparent tw-p-2 tw-opacity-0 tw-transition-opacity tw-duration-200 group-focus-within:tw-opacity-100 desktop-hover:group-hover:tw-opacity-100"><div className="tw-pointer-events-auto tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">{canOpenDrop && <div className="tw-flex tw-h-8 tw-items-center"><WaveDropActionsOpen drop={drop} /></div>}{isCuratable && <DropCurationButton dropId={drop.id} waveId={drop.wave.id} isCuratable={isCuratable} isCurated={isCurated} className="tw-bg-iron-950/70" />}{canShowVote && <VotingModalButton drop={drop} onClick={handleVoteButtonClick} variant="subtle" />}</div></div>;
    $[58] = canOpenDrop;
    $[59] = canShowVote;
    $[60] = drop;
    $[61] = isCuratable;
    $[62] = isCurated;
    $[63] = showDesktopContentOnlyActions;
    $[64] = t20;
  } else {
    t20 = $[64];
  }
  let t21;
  if ($[65] !== t14 || $[66] !== t18 || $[67] !== t19 || $[68] !== t20) {
    t21 = <div ref={setViewportEl} className={t14}>{t18}{t19}{t20}</div>;
    $[65] = t14;
    $[66] = t18;
    $[67] = t19;
    $[68] = t20;
    $[69] = t21;
  } else {
    t21 = $[69];
  }
  let t22;
  if ($[70] !== drop || $[71] !== isContentOnlyMode) {
    t22 = isContentOnlyMode && <WaveLeaderboardIdentity drop={drop} variant="responsive" cardVariant="chat" className="tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800/50 tw-bg-iron-950/50 tw-p-3" supplementFullWidth={true} />;
    $[70] = drop;
    $[71] = isContentOnlyMode;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  let t23;
  if ($[73] !== authorHandle || $[74] !== canShowVote || $[75] !== drop || $[76] !== hasUserVoted || $[77] !== isCompactMode || $[78] !== isNegativeVote || $[79] !== userVote || $[80] !== voteStyle || $[81] !== votingCreditLabel) {
    t23 = isCompactMode && <div data-testid={`wave-leaderboard-grid-item-footer-${drop.id}`} className="tw-rounded-b-lg tw-bg-iron-950/50 tw-px-3 tw-pb-3 tw-pt-3"><div className="tw-mb-1.5">{drop.title && <h3 className="tw-mb-0 tw-truncate tw-text-sm tw-font-bold tw-leading-tight tw-text-iron-200">{drop.title}</h3>}<div className="tw-flex tw-items-center tw-justify-between"><div className="tw-mr-2 tw-min-w-0 tw-flex-1">{authorHandle && <UserProfileTooltipWrapper user={authorHandle}><Link onClick={_temp4} href={`/${authorHandle}`} className="tw-text-xs tw-text-iron-400 tw-no-underline tw-transition-colors tw-duration-150 desktop-hover:hover:tw-text-iron-300 desktop-hover:hover:tw-underline">{authorHandle}</Link></UserProfileTooltipWrapper>}</div><GridItemRankBadge drop={drop} /></div></div><WaveLeaderboardIdentity drop={drop} variant="condensed" className="tw-mb-3" supplementFullWidth={true} /><div className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-text-xs"><WaveLeaderboardGalleryItemVotes drop={drop} variant="subtle" /><div className="tw-ml-4 tw-flex tw-items-center tw-gap-1 tw-text-iron-500"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" className="tw-size-3 tw-flex-shrink-0"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" /></svg><span className="tw-font-medium">{formatNumberWithCommas(drop.raters_count)}</span></div></div><div className="tw-flex tw-items-center tw-justify-between tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800/50 tw-pt-2">{hasUserVoted && <span className="tw-font-mono tw-text-[11px] tw-text-iron-500">You:{" "}<span className={voteStyle}>{isNegativeVote && "-"}{formatNumberWithCommas(Math.abs(userVote))}{" "}{votingCreditLabel}</span></span>}<div className="tw-ml-auto tw-flex tw-items-center tw-gap-1.5"><DropCurationButton dropId={drop.id} waveId={drop.wave.id} isCuratable={drop.context_profile_context?.curatable ?? false} isCurated={drop.context_profile_context?.curated ?? false} />{canShowVote && <VotingModalButton drop={drop} onClick={handleVoteButtonClick} variant="subtle" />}</div></div></div>;
    $[73] = authorHandle;
    $[74] = canShowVote;
    $[75] = drop;
    $[76] = hasUserVoted;
    $[77] = isCompactMode;
    $[78] = isNegativeVote;
    $[79] = userVote;
    $[80] = voteStyle;
    $[81] = votingCreditLabel;
    $[82] = t23;
  } else {
    t23 = $[82];
  }
  let t24;
  if ($[83] !== drop || $[84] !== isCompactMode || $[85] !== isContentOnlyMode || $[86] !== isMobileScreen || $[87] !== isVotingModalOpen) {
    t24 = (isCompactMode || isContentOnlyMode) && (isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} /> : <VotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} />);
    $[83] = drop;
    $[84] = isCompactMode;
    $[85] = isContentOnlyMode;
    $[86] = isMobileScreen;
    $[87] = isVotingModalOpen;
    $[88] = t24;
  } else {
    t24 = $[88];
  }
  let t25;
  if ($[89] !== canOpenDrop || $[90] !== canShowVote || $[91] !== drop || $[92] !== handleMobileCurateClick || $[93] !== handleMobileVoteClick || $[94] !== isActive || $[95] !== isCuratable || $[96] !== isCurated || $[97] !== isCurating || $[98] !== setIsActive || $[99] !== showMobileContentOnlyActions) {
    t25 = showMobileContentOnlyActions && createPortal(<CommonDropdownItemsMobileWrapper isOpen={isActive} setOpen={setIsActive}><div className="tw-grid tw-grid-cols-1 tw-gap-y-2">{canOpenDrop && <WaveDropMobileMenuOpen drop={drop} onOpenChange={() => setIsActive(false)} />}{isCuratable && <button type="button" onClick={handleMobileCurateClick} disabled={isCurating || drop.id.startsWith("temp-")} className={`tw-flex tw-select-none tw-items-center tw-gap-x-4 tw-rounded-xl tw-border-0 tw-bg-iron-950 tw-p-4 tw-text-left tw-transition-colors tw-duration-200 ${isCurating || drop.id.startsWith("temp-") ? "tw-cursor-default tw-opacity-60" : "active:tw-bg-iron-800"}`}><svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-iron-300" xmlns="http://www.w3.org/2000/svg"><path d="M8 12H16M8 16H13M8.6 21H15.4C16.5201 21 17.0802 21 17.508 20.782C17.8843 20.5903 18.1903 20.2843 18.382 19.908C18.6 19.4802 18.6 18.9201 18.6 17.8V8.2C18.6 7.07989 18.6 6.51984 18.382 6.09202C18.1903 5.71569 17.8843 5.40973 17.508 5.21799C17.0802 5 16.5201 5 15.4 5H8.6C7.47989 5 6.91984 5 6.49202 5.21799C6.11569 5.40973 5.80973 5.71569 5.61799 6.09202C5.4 6.51984 5.4 7.07989 5.4 8.2V17.8C5.4 18.9201 5.4 19.4802 5.61799 19.908C5.80973 20.2843 6.11569 20.5903 6.49202 20.782C6.91984 21 7.47989 21 8.6 21ZM15.75 3V7.5M13.5 5.25H18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg><span className="tw-text-base tw-font-semibold tw-text-iron-300">{isCurated ? "Uncurate drop" : "Curate drop"}</span></button>}{canShowVote && <button type="button" onClick={handleMobileVoteClick} className="tw-flex tw-select-none tw-items-center tw-gap-x-4 tw-rounded-xl tw-border-0 tw-bg-iron-950 tw-p-4 tw-text-left tw-transition-colors tw-duration-200 active:tw-bg-iron-800"><svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-iron-300" xmlns="http://www.w3.org/2000/svg"><path d="M12 2.75L14.8379 8.50092L21.1845 9.42395L16.5923 13.8991L17.6765 20.221L12 17.2379L6.32352 20.221L7.4077 13.8991L2.8155 9.42395L9.16215 8.50092L12 2.75Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg><span className="tw-text-base tw-font-semibold tw-text-iron-300">Vote</span></button>}</div></CommonDropdownItemsMobileWrapper>, document.body);
    $[89] = canOpenDrop;
    $[90] = canShowVote;
    $[91] = drop;
    $[92] = handleMobileCurateClick;
    $[93] = handleMobileVoteClick;
    $[94] = isActive;
    $[95] = isCuratable;
    $[96] = isCurated;
    $[97] = isCurating;
    $[98] = setIsActive;
    $[99] = showMobileContentOnlyActions;
    $[100] = t25;
  } else {
    t25 = $[100];
  }
  let t26;
  if ($[101] !== onCardClick || $[102] !== onCardKeyDown || $[103] !== t12 || $[104] !== t13 || $[105] !== t21 || $[106] !== t22 || $[107] !== t23 || $[108] !== t24 || $[109] !== t25) {
    t26 = <div role="button" tabIndex={0} data-testid={t12} onClick={onCardClick} onKeyDown={onCardKeyDown} className="tw-group tw-cursor-pointer tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-p-0 tw-transition desktop-hover:hover:tw-border-iron-700" {...t13}>{t21}{t22}{t23}{t24}{t25}</div>;
    $[101] = onCardClick;
    $[102] = onCardKeyDown;
    $[103] = t12;
    $[104] = t13;
    $[105] = t21;
    $[106] = t22;
    $[107] = t23;
    $[108] = t24;
    $[109] = t25;
    $[110] = t26;
  } else {
    t26 = $[110];
  }
  return t26;
};
function _temp3() {}
function _temp4(e) {
  return e.stopPropagation();
}